import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Space, 
  Tag, 
  Spin, 
  Alert,
  Table,
  Statistic,
  Empty,
  Tabs,
  Select,
  Input,
  Avatar,
  message,
  Modal
} from 'antd';
import {
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  CalendarOutlined as EventOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  EditOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useMunicipalityDetails } from '../hooks/useMunicipalityDetails';
import { useUsersByMunicipality } from '../hooks/useAllUsersAndInvitations';
import { useMunicipalityStatistics } from '../hooks/useMunicipalityStatistics';
import { useMunicipalityNews } from '../hooks/useMunicipalityNews';
import { useMunicipalityEvents, Event, EventStatus } from '../hooks/useMunicipalityEvents';
import { useMunicipalitySurveys, Survey, SurveyStatus } from '../hooks/useMunicipalitySurveys';
import { useMunicipalityPOIs, POI, POIStatus, POICategory } from '../hooks/useMunicipalityPOIs';
import { useMunicipalityIssues, Issue, IssueStatus, IssuePriority, IssueCategory } from '../hooks/useMunicipalityIssues';
import { useMunicipalityLostAndFound, LostAndFoundRecord, LostAndFoundType, LostAndFoundStatus, LostAndFoundCategory } from '../hooks/useMunicipalityLostAndFound';
import { useMunicipalityVolunteering, VolunteeringActivity, VolunteeringStatus } from '../hooks/useMunicipalityVolunteering';
import { useMunicipalityCouncil, MunicipalCouncilor } from '../hooks/useMunicipalityCouncil';
import { useMunicipalityMayor, Mayor } from '../hooks/useMunicipalityMayor';
import { useMunicipalityCommittees, MunicipalCommittee } from '../hooks/useMunicipalityCommittees';
import { useMunicipalityCommitteeMembers, CommitteeMember } from '../hooks/useMunicipalityCommitteeMembers';
import { useMunicipalitySocialMedia } from '../hooks/useMunicipalitySocialMedia';
import { UserRole, NewsArticle, NewsStatus, NewsCategory } from '@municipality/types';
import { AllUser } from '../hooks/useAllUsersAndInvitations';
import { useResponsive } from '@municipality/ui';
import { sanitizeHTML } from '../utils/sanitization';
import { municipalitiesService } from '../services/municipalities';
import styled from '@emotion/styled';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig, FilterValue, SorterResult } from 'antd/es/table/interface';

const { Title, Text } = Typography;

const InfoCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  height: 100%;
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled(Text)`
  font-size: 14px;
  color: #666;
  min-width: 120px;
  display: block;
`;

const InfoValue = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
`;

const UserManagementCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const StatisticsCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
  height: 100%;
  
  .ant-statistic-title {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }
  
  .ant-statistic-content {
    font-size: 28px;
    font-weight: bold;
    color: #1890ff;
  }
`;

// Date formatting helpers (matching municipality app format)
const formatDateShort = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateTime = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const MunicipalityDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['municipalities', 'users']);
  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState<string>('details');
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [newsSearchText, setNewsSearchText] = useState<string>('');
  const [newsFilteredInfo, setNewsFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [eventsSearchText, setEventsSearchText] = useState<string>('');
  const [eventsFilteredInfo, setEventsFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [surveysSearchText, setSurveysSearchText] = useState<string>('');
  const [surveysFilteredInfo, setSurveysFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [poisSearchText, setPoisSearchText] = useState<string>('');
  const [poisFilteredInfo, setPoisFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [issuesSearchText, setIssuesSearchText] = useState<string>('');
  const [issuesFilteredInfo, setIssuesFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [lostAndFoundSearchText, setLostAndFoundSearchText] = useState<string>('');
  const [lostAndFoundFilteredInfo, setLostAndFoundFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [volunteeringSearchText, setVolunteeringSearchText] = useState<string>('');
  const [volunteeringFilteredInfo, setVolunteeringFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  
  const { municipality, loading: municipalityLoading, error: municipalityError } = useMunicipalityDetails(id || null);
  const { users: allUsers, loading: usersLoading } = useUsersByMunicipality(id || '');
  const { statistics, loading: statsLoading } = useMunicipalityStatistics(id || null);
  const { data: newsData, loading: newsLoading, error: newsError } = useMunicipalityNews(id || null);
  const { data: eventsData, loading: eventsLoading, error: eventsError } = useMunicipalityEvents(id || null);
  const { data: surveysData, loading: surveysLoading, error: surveysError } = useMunicipalitySurveys(id || null);
  const { data: poisData, loading: poisLoading, error: poisError } = useMunicipalityPOIs(id || null);
  const { data: issuesData, loading: issuesLoading, error: issuesError } = useMunicipalityIssues(id || null);
  const { data: lostAndFoundData, loading: lostAndFoundLoading, error: lostAndFoundError } = useMunicipalityLostAndFound(id || null);
  const { data: volunteeringData, loading: volunteeringLoading, error: volunteeringError } = useMunicipalityVolunteering(id || null);
  const { data: councilData, loading: councilLoading, error: councilError } = useMunicipalityCouncil(id || null);
  const { data: mayorData, loading: mayorLoading, error: mayorError } = useMunicipalityMayor(id || null);
  const { data: committeesData, loading: committeesLoading, error: committeesError } = useMunicipalityCommittees(id || null);
  const { data: committeeMembersData, loading: committeeMembersLoading, error: committeeMembersError } = useMunicipalityCommitteeMembers(id || null);
  const { data: socialMediaData, loading: socialMediaLoading, error: socialMediaError } = useMunicipalitySocialMedia(id || null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'pending':
        return 'orange';
      case 'inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleOutlined />;
      case 'pending':
        return <ClockCircleOutlined />;
      case 'inactive':
        return <CloseCircleOutlined />;
      default:
        return null;
    }
  };

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return 'Not available';
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Handle status change with confirmation
  const handleStatusChange = async (newStatus: 'trial' | 'provisional' | 'active') => {
    if (!municipality || !id) return;
    
    // Validate status
    if (!['trial', 'provisional', 'active'].includes(newStatus)) {
      message.error(t('settings.status.invalidStatus', 'Invalid status value'));
      return;
    }
    
    // Skip if status hasn't changed
    if (municipality.status === newStatus) return;
    
    const oldStatus = municipality.status;
    
    // Show confirmation for status changes (especially downgrades)
    const isDowngrade = oldStatus === 'active' && (newStatus === 'trial' || newStatus === 'provisional');
    
    if (isDowngrade) {
      Modal.confirm({
        title: t('settings.status.confirmChange', 'Confirm status change'),
        content: t('settings.status.confirmDowngrade', 
          `Changing status from "${oldStatus}" to "${newStatus}" will hide this municipality from citizens. Are you sure?`),
        okText: t('common.confirm', 'Confirm'),
        cancelText: t('common.cancel', 'Cancel'),
        okType: 'danger',
        onOk: async () => {
          await updateStatus(newStatus);
        }
      });
    } else {
      await updateStatus(newStatus);
    }
  };
  
  const updateStatus = async (newStatus: 'trial' | 'provisional' | 'active') => {
    if (!municipality || !id) return;
    
    try {
      setUpdatingStatus(true);
      await municipalitiesService.updateMunicipality(id, { status: newStatus });
      message.success(t('settings.status.updateSuccess', 'Status updated successfully'));
      
      // Refresh the page to get updated data
      window.location.reload();
    } catch (error) {
      console.error('Failed to update municipality status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update status';
      message.error(t('settings.status.updateError', `Failed to update status: ${errorMessage}`));
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Separate users into staff
  const staffUsers = useMemo(() => {
    return allUsers.filter(user => 
      user.role === UserRole.MUNICIPALITY_ADMIN || 
      user.role === UserRole.MUNICIPALITY_USER
    );
  }, [allUsers]);

  const getRoleColor = (role: string) => {
    switch (role) {
      case UserRole.MUNICIPALITY_ADMIN:
        return 'blue';
      case UserRole.MUNICIPALITY_USER:
        return 'green';
      case UserRole.CITIZEN:
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case UserRole.MUNICIPALITY_ADMIN:
        return t('users:municipalityAdmin', 'Municipality Admin');
      case UserRole.MUNICIPALITY_USER:
        return t('users:municipalityUser', 'Municipality User');
      case UserRole.CITIZEN:
        return t('users:citizen', 'Citizen');
      default:
        return role;
    }
  };

  const getUserDisplayName = (user: AllUser) => {
    return user.displayName || user.name || user.email;
  };

  // News helper functions
  const getNewsStatusColor = (status: NewsStatus) => {
    switch (status) {
      case NewsStatus.PUBLISHED:
        return 'green';
      case NewsStatus.DRAFT:
        return 'orange';
      case NewsStatus.ARCHIVED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getNewsStatusLabel = (status: NewsStatus) => {
    switch (status) {
      case NewsStatus.PUBLISHED:
        return t('news.status.published', 'Published');
      case NewsStatus.DRAFT:
        return t('news.status.draft', 'Draft');
      case NewsStatus.ARCHIVED:
        return t('news.status.archived', 'Archived');
      default:
        return status;
    }
  };

  const getNewsCategoryColor = (category: NewsCategory) => {
    switch (category) {
      case NewsCategory.ANNOUNCEMENT:
        return '#722ed1';
      case NewsCategory.EVENT:
        return '#1890ff';
      case NewsCategory.NEWS:
        return '#52c41a';
      case NewsCategory.ALERT:
        return '#f5222d';
      case NewsCategory.UPDATE:
        return '#13c2c2';
      default:
        return '#8c8c8c';
    }
  };

  const getNewsCategoryLabel = (category: NewsCategory) => {
    switch (category) {
      case NewsCategory.ANNOUNCEMENT:
        return t('news.category.announcement', 'Announcement');
      case NewsCategory.EVENT:
        return t('news.category.event', 'Event');
      case NewsCategory.NEWS:
        return t('news.category.news', 'News');
      case NewsCategory.ALERT:
        return t('news.category.alert', 'Alert');
      case NewsCategory.UPDATE:
        return t('news.category.update', 'Update');
      default:
        return category;
    }
  };

  const getNewsTitle = (article: NewsArticle): string => {
    const translations = article.translations || {};
    return translations.en?.title || translations.el?.title || 'No title';
  };


  // Helper function to format event dates (matching municipality app)
  const formatEventDates = (event: Event) => {
    const startDisplay = event.startDateDisplay || (event.startDate ? formatDateTime(event.startDate) : '-');
    const endDisplay = event.endDateDisplay || (event.endDate ? formatDateTime(event.endDate) : '-');
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div>{startDisplay}</div>
        <div>{endDisplay}</div>
      </div>
    );
  };

  // Events helper functions
  const getEventStatusColor = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PUBLISHED:
        return 'green';
      case EventStatus.DRAFT:
        return 'orange';
      case EventStatus.CANCELLED:
        return 'red';
      case EventStatus.COMPLETED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getEventStatusLabel = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PUBLISHED:
        return t('events.status.published', 'Published');
      case EventStatus.DRAFT:
        return t('events.status.draft', 'Draft');
      case EventStatus.CANCELLED:
        return t('events.status.cancelled', 'Cancelled');
      case EventStatus.COMPLETED:
        return t('events.status.completed', 'Completed');
      default:
        return status;
    }
  };


  const getEventTitle = (event: Event): string => {
    const translations = event.translations || {};
    return translations.en?.title || translations.el?.title || 'No title';
  };

  // Surveys helper functions
  const getSurveyStatusColor = (status: SurveyStatus) => {
    switch (status) {
      case SurveyStatus.DRAFT:
        return 'default';
      case SurveyStatus.SCHEDULED:
        return 'orange';
      case SurveyStatus.IN_PROGRESS:
        return 'blue';
      case SurveyStatus.COMPLETED:
        return 'purple';
      case SurveyStatus.STOPPED:
        return 'red';
      default:
        return 'default';
    }
  };

  const getSurveyStatusLabel = (status: SurveyStatus) => {
    switch (status) {
      case SurveyStatus.DRAFT:
        return t('surveys.status.draft', 'Draft');
      case SurveyStatus.SCHEDULED:
        return t('surveys.status.scheduled', 'Scheduled');
      case SurveyStatus.IN_PROGRESS:
        return t('surveys.status.inProgress', 'In Progress');
      case SurveyStatus.COMPLETED:
        return t('surveys.status.completed', 'Completed');
      case SurveyStatus.STOPPED:
        return t('surveys.status.stopped', 'Stopped');
      default:
        return status;
    }
  };

  const getSurveyTitle = (survey: Survey): string => {
    const translations = survey.translations || {};
    return translations.en?.title || translations.el?.title || 'No title';
  };

  // Helper function to format survey dates (matching municipality app)
  const formatSurveyDates = (survey: Survey) => {
    const startDisplay = survey.startDateReadable || formatDateTime(survey.startDate);
    const endDisplay = survey.endDateReadable || formatDateTime(survey.endDate);
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div>{startDisplay}</div>
        <div>{endDisplay}</div>
      </div>
    );
  };

  // POI helper functions
  const getPOIStatusColor = (status: POIStatus) => {
    switch (status) {
      case POIStatus.PUBLISHED:
        return 'green';
      case POIStatus.DRAFT:
        return 'orange';
      case POIStatus.ARCHIVED:
        return 'red';
      case POIStatus.UNDER_REVIEW:
        return 'blue';
      default:
        return 'default';
    }
  };

  const getPOIStatusLabel = (status: POIStatus) => {
    switch (status) {
      case POIStatus.DRAFT:
        return t('poi.status.draft', 'Draft');
      case POIStatus.PUBLISHED:
        return t('poi.status.published', 'Published');
      case POIStatus.ARCHIVED:
        return t('poi.status.archived', 'Archived');
      case POIStatus.UNDER_REVIEW:
        return t('poi.status.underReview', 'Under review');
      default:
        return status;
    }
  };

  const getPOITitle = (poi: POI): string => {
    const translations = poi.translations || {};
    return translations.en?.title || translations.el?.title || 'No title';
  };

  // Issues helper functions
  const getIssueStatusColor = (status: IssueStatus) => {
    switch (status) {
      case IssueStatus.REPORTED:
        return 'blue';
      case IssueStatus.ASSIGNED:
        return 'purple';
      case IssueStatus.ON_HOLD:
        return 'orange';
      case IssueStatus.IN_PROGRESS:
        return 'green';
      case IssueStatus.RESOLVED:
        return 'default';
      case IssueStatus.CANCELLED:
        return 'red';
      case IssueStatus.ARCHIVED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getIssueStatusLabel = (status: IssueStatus) => {
    switch (status) {
      case IssueStatus.REPORTED:
        return t('issues.status.reported', 'Reported');
      case IssueStatus.ASSIGNED:
        return t('issues.status.assigned', 'Assigned');
      case IssueStatus.ON_HOLD:
        return t('issues.status.onHold', 'On Hold');
      case IssueStatus.IN_PROGRESS:
        return t('issues.status.inProgress', 'In Progress');
      case IssueStatus.RESOLVED:
        return t('issues.status.resolved', 'Resolved');
      case IssueStatus.CANCELLED:
        return t('issues.status.cancelled', 'Cancelled');
      case IssueStatus.ARCHIVED:
        return t('issues.status.archived', 'Archived');
      default:
        return status;
    }
  };

  const getIssuePriorityColor = (priority: IssuePriority) => {
    switch (priority) {
      case IssuePriority.URGENT:
        return 'red';
      case IssuePriority.HIGH:
        return 'orange';
      case IssuePriority.MEDIUM:
        return 'gold';
      case IssuePriority.LOW:
        return 'green';
      default:
        return 'default';
    }
  };

  const getIssuePriorityLabel = (priority: IssuePriority) => {
    switch (priority) {
      case IssuePriority.URGENT:
        return t('issues.priority.urgent', 'Urgent');
      case IssuePriority.HIGH:
        return t('issues.priority.high', 'High');
      case IssuePriority.MEDIUM:
        return t('issues.priority.medium', 'Medium');
      case IssuePriority.LOW:
        return t('issues.priority.low', 'Low');
      default:
        return priority;
    }
  };

  const formatIssueId = (id: string): string => {
    return id;
  };

  // Lost & Found helper functions
  const getLostAndFoundStatusColor = (status: LostAndFoundStatus) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'published':
        return 'green';
      case 'rejected':
        return 'red';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getLostAndFoundStatusLabel = (status: LostAndFoundStatus) => {
    switch (status) {
      case 'pending':
        return t('lostAndFound.status.pending', 'Pending');
      case 'published':
        return t('lostAndFound.status.published', 'Published');
      case 'rejected':
        return t('lostAndFound.status.rejected', 'Rejected');
      case 'archived':
        return t('lostAndFound.status.archived', 'Archived');
      default:
        return status;
    }
  };

  const getLostAndFoundTypeLabel = (type: LostAndFoundType) => {
    return type === 'lost' 
      ? t('lostAndFound.type.lost', 'Lost')
      : t('lostAndFound.type.found', 'Found');
  };

  // Volunteering helper functions
  const getVolunteeringStatusColor = (status: VolunteeringStatus) => {
    switch (status) {
      case VolunteeringStatus.DRAFT:
        return 'default';
      case VolunteeringStatus.SCHEDULED:
        return 'orange';
      case VolunteeringStatus.IN_PROGRESS:
        return 'blue';
      case VolunteeringStatus.COMPLETED:
        return 'green';
      case VolunteeringStatus.STOPPED:
        return 'red';
      default:
        return 'default';
    }
  };

  const getVolunteeringStatusLabel = (status: VolunteeringStatus) => {
    switch (status) {
      case VolunteeringStatus.DRAFT:
        return t('volunteering.status.draft', 'Draft');
      case VolunteeringStatus.SCHEDULED:
        return t('volunteering.status.scheduled', 'Scheduled');
      case VolunteeringStatus.IN_PROGRESS:
        return t('volunteering.status.inProgress', 'In Progress');
      case VolunteeringStatus.COMPLETED:
        return t('volunteering.status.completed', 'Completed');
      case VolunteeringStatus.STOPPED:
        return t('volunteering.status.stopped', 'Stopped');
      default:
        return status;
    }
  };

  const getVolunteeringTitle = (activity: VolunteeringActivity): string => {
    const translations = activity.translations || {};
    return translations.en?.title || translations.el?.title || 'No title';
  };

  const formatVolunteeringDates = (activity: VolunteeringActivity) => {
    const startDisplay = activity.startDateDisplay || formatDateTime(activity.startDate);
    const endDisplay = activity.endDateDisplay || formatDateTime(activity.endDate);
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div>{startDisplay}</div>
        <div>{endDisplay}</div>
      </div>
    );
  };

  // Governance helper functions
  const getCouncilorName = (councilor: MunicipalCouncilor): string => {
    const translations = councilor.translations || {};
    const name = translations.en?.name || translations.el?.name || councilor.name;
    const surname = translations.en?.surname || translations.el?.surname || councilor.surname;
    return `${name} ${surname}`.trim();
  };

  const getMayorName = (mayor: Mayor): string => {
    const translations = mayor.translations || {};
    const name = translations.en?.name || translations.el?.name || mayor.name;
    const surname = translations.en?.surname || translations.el?.surname || mayor.surname;
    return `${name} ${surname}`.trim();
  };

  const getCommitteeName = (committee: MunicipalCommittee): string => {
    const translations = committee.translations || {};
    return translations.en?.name || translations.el?.name || committee.name;
  };

  const getCommitteeMemberName = (member: CommitteeMember): string => {
    const translations = member.translations || {};
    const name = translations.en?.name || translations.el?.name || member.name;
    const surname = translations.en?.surname || translations.el?.surname || member.surname;
    return `${name} ${surname}`.trim();
  };

  // Events table columns
  const eventsColumns: ColumnsType<Event> = [
    {
      title: t('events.columns.title', 'Title'),
      dataIndex: 'translations',
      key: 'title',
      ellipsis: true,
      render: (_translations: Event['translations'], record: Event) => {
        const title = getEventTitle(record);
        return <div style={{ fontWeight: 500 }}>{title}</div>;
      },
    },
    {
      title: t('events.columns.dates', 'Dates'),
      dataIndex: 'startDate',
      key: 'dates',
      width: 180,
      render: (_: unknown, record: Event) => formatEventDates(record),
    },
    {
      title: t('events.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: EventStatus) => (
        <Tag color={getEventStatusColor(status)}>
          {getEventStatusLabel(status)}
        </Tag>
      ),
      filters: eventsData?.items ? Array.from(new Set(eventsData.items.map((e) => e.status))).map((status) => ({
        text: getEventStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: eventsFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: Event) => record.status === value,
    },
    {
      title: t('events.columns.createdBy', 'Created By'),
      dataIndex: 'authorName',
      key: 'createdBy',
      width: 120,
      ellipsis: true,
      render: (authorName: string) => authorName || '-',
      sorter: (a, b) => (a.authorName || '').localeCompare(b.authorName || ''),
    },
  ];

  // Events table change handler
  const handleEventsTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<Event> | SorterResult<Event>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === null) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setEventsFilteredInfo(normalizedFilters);
  };

  // Surveys table columns
  const surveysColumns: ColumnsType<Survey> = [
    {
      title: t('surveys.columns.title', 'Title'),
      dataIndex: 'translations',
      key: 'title',
      width: 200,
      render: (_translations: Survey['translations'], record: Survey) => {
        const title = getSurveyTitle(record);
        return (
          <div style={{ 
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.4',
            wordBreak: 'break-word'
          }}>
            {title}
          </div>
        );
      },
    },
    {
      title: t('surveys.columns.dates', 'Start/End Dates'),
      dataIndex: 'startDate',
      key: 'dates',
      width: 180,
      render: (_: unknown, record: Survey) => formatSurveyDates(record),
      sorter: (a, b) => a.startDate.getTime() - b.startDate.getTime(),
    },
    {
      title: t('surveys.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: SurveyStatus) => (
        <Tag color={getSurveyStatusColor(status)}>
          {getSurveyStatusLabel(status)}
        </Tag>
      ),
      filters: surveysData?.items ? Array.from(new Set(surveysData.items.map((s) => s.status))).map((status) => ({
        text: getSurveyStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: surveysFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: Survey) => record.status === value,
    },
    {
      title: t('surveys.columns.responseCount', 'Responses'),
      dataIndex: 'responseCount',
      key: 'responseCount',
      width: 100,
      align: 'center' as const,
      render: (count: number) => count || 0,
      sorter: (a, b) => (a.responseCount || 0) - (b.responseCount || 0),
    },
  ];

  // Surveys table change handler
  const handleSurveysTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<Survey> | SorterResult<Survey>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === null) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setSurveysFilteredInfo(normalizedFilters);
  };

  // POI table columns
  const poisColumns: ColumnsType<POI> = [
    {
      title: t('poi.columns.title', 'Title'),
      dataIndex: 'translations',
      key: 'title',
      width: 200,
      render: (_translations: POI['translations'], record: POI) => {
        const title = getPOITitle(record);
        return (
          <div style={{ 
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.4',
            wordBreak: 'break-word'
          }}>
            {title}
          </div>
        );
      },
    },
    {
      title: t('poi.columns.category', 'Category'),
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category: POICategory) => (
        <Tag color="default">
          {t(`poi.categories.${category}`, category)}
        </Tag>
      ),
      filters: poisData?.items ? Array.from(new Set(poisData.items.map((p) => p.category))).map((category) => ({
        text: t(`poi.categories.${category}`, category),
        value: category,
      })) : [],
      filteredValue: poisFilteredInfo.category || null,
      onFilter: (value: React.Key | boolean, record: POI) => record.category === value,
    },
    {
      title: t('poi.columns.address', 'Address'),
      dataIndex: 'address',
      key: 'address',
      width: 200,
      ellipsis: true,
      render: (address: string) => address || '-',
    },
    {
      title: t('poi.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: POIStatus) => (
        <Tag color={getPOIStatusColor(status)}>
          {getPOIStatusLabel(status)}
        </Tag>
      ),
      filters: poisData?.items ? Array.from(new Set(poisData.items.map((p) => p.status))).map((status) => ({
        text: getPOIStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: poisFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: POI) => record.status === value,
    },
    {
      title: t('poi.columns.viewCount', 'Views'),
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: 100,
      align: 'center' as const,
      render: (count: number) => count || 0,
      sorter: (a, b) => (a.viewCount || 0) - (b.viewCount || 0),
    },
  ];

  // POI table change handler
  const handlePOIsTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<POI> | SorterResult<POI>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === null) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setPoisFilteredInfo(normalizedFilters);
  };

  // Issues table columns
  const issuesColumns: ColumnsType<Issue> = [
    {
      title: t('issues.columns.id', 'ID'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: string) => `#${formatIssueId(id)}`,
      sorter: (a, b) => parseInt(a.id, 10) - parseInt(b.id, 10),
    },
    {
      title: t('issues.columns.title', 'Title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (title: string) => (
        <div style={{ 
          fontWeight: 500,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: '1.4',
          wordBreak: 'break-word'
        }}>
          {title}
        </div>
      ),
    },
    {
      title: t('issues.columns.category', 'Category'),
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category: IssueCategory) => (
        <Tag color="default">
          {t(`issues.categories.${category}`, category)}
        </Tag>
      ),
      filters: issuesData?.items ? Array.from(new Set(issuesData.items.map((i) => i.category))).map((category) => ({
        text: t(`issues.categories.${category}`, category),
        value: category,
      })) : [],
      filteredValue: issuesFilteredInfo.category || null,
      onFilter: (value: React.Key | boolean, record: Issue) => record.category === value,
    },
    {
      title: t('issues.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: IssueStatus) => (
        <Tag color={getIssueStatusColor(status)}>
          {getIssueStatusLabel(status)}
        </Tag>
      ),
      filters: issuesData?.items ? Array.from(new Set(issuesData.items.map((i) => i.status))).map((status) => ({
        text: getIssueStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: issuesFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: Issue) => record.status === value,
    },
    {
      title: t('issues.columns.priority', 'Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: IssuePriority) => (
        <Tag color={getIssuePriorityColor(priority)}>
          {getIssuePriorityLabel(priority)}
        </Tag>
      ),
      filters: issuesData?.items ? Array.from(new Set(issuesData.items.map((i) => i.priority))).map((priority) => ({
        text: getIssuePriorityLabel(priority),
        value: priority,
      })) : [],
      filteredValue: issuesFilteredInfo.priority || null,
      onFilter: (value: React.Key | boolean, record: Issue) => record.priority === value,
    },
    {
      title: t('issues.columns.dueDate', 'Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (dueDate: Date | null, record: Issue) => {
        if (!dueDate) {
          return <span style={{ color: '#8c8c8c' }}>—</span>;
        }
        const isOverdue = new Date(dueDate) < new Date() && 
          record.status !== IssueStatus.RESOLVED && 
          record.status !== IssueStatus.CANCELLED;
        return (
          <div>
            <span style={{ color: isOverdue ? '#ff4d4f' : 'inherit' }}>
              {formatDateShort(dueDate)}
            </span>
            {isOverdue && (
              <div style={{ fontSize: '11px', color: '#ff4d4f' }}>
                {t('issues.overdue', 'Overdue')}
              </div>
            )}
          </div>
        );
      },
      sorter: (a, b) => {
        const aTime = a.dueDate ? a.dueDate.getTime() : 0;
        const bTime = b.dueDate ? b.dueDate.getTime() : 0;
        return aTime - bTime;
      },
    },
    {
      title: t('issues.columns.reportedBy', 'Reported By'),
      dataIndex: 'reportedBy',
      key: 'reportedBy',
      width: 150,
      render: (reportedBy: Issue['reportedBy']) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '13px' }}>
            {`${reportedBy.firstName} ${reportedBy.lastName.charAt(0)}.`}
          </div>
          <small style={{ color: '#888', fontSize: '12px' }}>{reportedBy.email}</small>
        </div>
      ),
    },
  ];

  // Issues table change handler
  const handleIssuesTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<Issue> | SorterResult<Issue>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === null) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setIssuesFilteredInfo(normalizedFilters);
  };

  // Lost & Found table columns
  const lostAndFoundColumns: ColumnsType<LostAndFoundRecord> = [
    {
      title: t('lostAndFound.columns.title', 'Title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (title: string) => (
        <div style={{ 
          fontWeight: 500,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: '1.4',
          wordBreak: 'break-word'
        }}>
          {title}
        </div>
      ),
    },
    {
      title: t('lostAndFound.columns.type', 'Type'),
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: LostAndFoundType) => (
        <Tag color={type === 'lost' ? 'red' : 'green'}>
          {getLostAndFoundTypeLabel(type)}
        </Tag>
      ),
      filters: lostAndFoundData?.items ? Array.from(new Set(lostAndFoundData.items.map((r) => r.type))).map((type) => ({
        text: getLostAndFoundTypeLabel(type),
        value: type,
      })) : [],
      filteredValue: lostAndFoundFilteredInfo.type || null,
      onFilter: (value: React.Key | boolean, record: LostAndFoundRecord) => record.type === value,
    },
    {
      title: t('lostAndFound.columns.category', 'Category'),
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category: LostAndFoundCategory) => (
        <Tag color="default">
          {t(`lostAndFound.categories.${category}`, category)}
        </Tag>
      ),
      filters: lostAndFoundData?.items ? Array.from(new Set(lostAndFoundData.items.map((r) => r.category))).map((category) => ({
        text: t(`lostAndFound.categories.${category}`, category),
        value: category,
      })) : [],
      filteredValue: lostAndFoundFilteredInfo.category || null,
      onFilter: (value: React.Key | boolean, record: LostAndFoundRecord) => record.category === value,
    },
    {
      title: t('lostAndFound.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: LostAndFoundStatus) => (
        <Tag color={getLostAndFoundStatusColor(status)}>
          {getLostAndFoundStatusLabel(status)}
        </Tag>
      ),
      filters: lostAndFoundData?.items ? Array.from(new Set(lostAndFoundData.items.map((r) => r.status))).map((status) => ({
        text: getLostAndFoundStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: lostAndFoundFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: LostAndFoundRecord) => record.status === value,
    },
    {
      title: t('lostAndFound.columns.viewCount', 'Views'),
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: 100,
      align: 'center' as const,
      render: (count: number) => count || 0,
      sorter: (a, b) => (a.viewCount || 0) - (b.viewCount || 0),
    },
  ];

  // Lost & Found table change handler
  const handleLostAndFoundTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<LostAndFoundRecord> | SorterResult<LostAndFoundRecord>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === null) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setLostAndFoundFilteredInfo(normalizedFilters);
  };

  // Volunteering table columns
  const volunteeringColumns: ColumnsType<VolunteeringActivity> = [
    {
      title: t('volunteering.columns.title', 'Title'),
      dataIndex: 'translations',
      key: 'title',
      width: 200,
      render: (_translations: VolunteeringActivity['translations'], record: VolunteeringActivity) => {
        const title = getVolunteeringTitle(record);
        return (
          <div style={{ 
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.4',
            wordBreak: 'break-word'
          }}>
            {title}
          </div>
        );
      },
    },
    {
      title: t('volunteering.columns.dates', 'Start/End Dates'),
      dataIndex: 'startDate',
      key: 'dates',
      width: 180,
      render: (_: unknown, record: VolunteeringActivity) => formatVolunteeringDates(record),
      sorter: (a, b) => a.startDate.getTime() - b.startDate.getTime(),
    },
    {
      title: t('volunteering.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: VolunteeringStatus) => (
        <Tag color={getVolunteeringStatusColor(status)}>
          {getVolunteeringStatusLabel(status)}
        </Tag>
      ),
      filters: volunteeringData?.items ? Array.from(new Set(volunteeringData.items.map((a) => a.status))).map((status) => ({
        text: getVolunteeringStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: volunteeringFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: VolunteeringActivity) => record.status === value,
    },
    {
      title: t('volunteering.columns.registrations', 'Registrations'),
      dataIndex: 'registrationCount',
      key: 'registrationCount',
      width: 120,
      align: 'center' as const,
      render: (count: number, record: VolunteeringActivity) => (
        <div>
          <div>{count || 0}</div>
          {record.maxParticipants && (
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
              / {record.maxParticipants}
            </div>
          )}
        </div>
      ),
      sorter: (a, b) => (a.registrationCount || 0) - (b.registrationCount || 0),
    },
  ];

  // Volunteering table change handler
  const handleVolunteeringTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<VolunteeringActivity> | SorterResult<VolunteeringActivity>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value === null) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setVolunteeringFilteredInfo(normalizedFilters);
  };

  // Council table columns
  const councilColumns: ColumnsType<MunicipalCouncilor> = [
    {
      title: t('governance.council.columns.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (_: unknown, record: MunicipalCouncilor) => getCouncilorName(record),
    },
    {
      title: t('governance.council.columns.email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string | undefined) => email || '-',
    },
    {
      title: t('governance.council.columns.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => formatDateShort(date),
      sorter: (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
    },
  ];

  // Committees table columns
  const committeesColumns: ColumnsType<MunicipalCommittee> = [
    {
      title: t('governance.committees.columns.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (_: unknown, record: MunicipalCommittee) => getCommitteeName(record),
    },
    {
      title: t('governance.committees.columns.members', 'Members'),
      dataIndex: 'assignedMembers',
      key: 'members',
      width: 150,
      render: (members: MunicipalCommittee['assignedMembers']) => {
        if (!members || members.length === 0) {
          return 0;
        }
        return members.length;
      },
    },
    {
      title: t('governance.committees.columns.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => formatDateShort(date),
      sorter: (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
    },
  ];

  // Committee Members table columns
  const committeeMembersColumns: ColumnsType<CommitteeMember> = [
    {
      title: t('governance.committeeMembers.columns.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (_: unknown, record: CommitteeMember) => getCommitteeMemberName(record),
    },
    {
      title: t('governance.committeeMembers.columns.email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string | undefined) => email || '-',
    },
    {
      title: t('governance.committeeMembers.columns.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => formatDateShort(date),
      sorter: (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
    },
  ];

  // Normalize strings for Greek-friendly search
  const normalizeGreekSearch = (text: string): string => {
    if (!text) return '';
    let s = text.toLowerCase();
    s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    s = s.replace(/ς/g, 'σ');
    return s;
  };

  // Filtered and searched news items
  const filteredNewsItems = useMemo(() => {
    if (!newsData?.items) return [];
    let items = [...newsData.items];

    // Apply search filter
    if (newsSearchText.trim()) {
      const needle = normalizeGreekSearch(newsSearchText.trim());
      items = items.filter((item) => {
        const title = getNewsTitle(item);
        return normalizeGreekSearch(title).includes(needle);
      });
    }

    // Apply status filter
    if (newsFilteredInfo.status && newsFilteredInfo.status.length > 0) {
      items = items.filter((item) => newsFilteredInfo.status?.includes(item.status));
    }

    // Apply category filter
    if (newsFilteredInfo.category && newsFilteredInfo.category.length > 0) {
      items = items.filter((item) => newsFilteredInfo.category?.includes(item.category));
    }

    return items;
  }, [newsData?.items, newsSearchText, newsFilteredInfo]);

  // Filtered and searched events items
  const filteredEventsItems = useMemo(() => {
    if (!eventsData?.items) return [];
    let items = [...eventsData.items];

    // Apply search filter
    if (eventsSearchText.trim()) {
      const needle = normalizeGreekSearch(eventsSearchText.trim());
      items = items.filter((item) => {
        const title = getEventTitle(item);
        return normalizeGreekSearch(title).includes(needle);
      });
    }

    // Apply status filter
    if (eventsFilteredInfo.status && eventsFilteredInfo.status.length > 0) {
      items = items.filter((item) => eventsFilteredInfo.status?.includes(item.status));
    }

    return items;
  }, [eventsData?.items, eventsSearchText, eventsFilteredInfo]);

  // Filtered and searched surveys items
  const filteredSurveysItems = useMemo(() => {
    if (!surveysData?.items) return [];
    let items = [...surveysData.items];

    // Apply search filter
    if (surveysSearchText.trim()) {
      const needle = normalizeGreekSearch(surveysSearchText.trim());
      items = items.filter((item) => {
        const title = getSurveyTitle(item);
        return normalizeGreekSearch(title).includes(needle);
      });
    }

    // Apply status filter
    if (surveysFilteredInfo.status && surveysFilteredInfo.status.length > 0) {
      items = items.filter((item) => surveysFilteredInfo.status?.includes(item.status));
    }

    return items;
  }, [surveysData?.items, surveysSearchText, surveysFilteredInfo]);

  // Filtered and searched POIs items
  const filteredPOIsItems = useMemo(() => {
    if (!poisData?.items) return [];
    let items = [...poisData.items];

    // Apply search filter (by title and address)
    if (poisSearchText.trim()) {
      const needle = normalizeGreekSearch(poisSearchText.trim());
      items = items.filter((item) => {
        const title = getPOITitle(item);
        const address = normalizeGreekSearch(item.address || '');
        return normalizeGreekSearch(title).includes(needle) || address.includes(needle);
      });
    }

    // Apply status filter
    if (poisFilteredInfo.status && poisFilteredInfo.status.length > 0) {
      items = items.filter((item) => poisFilteredInfo.status?.includes(item.status));
    }

    // Apply category filter
    if (poisFilteredInfo.category && poisFilteredInfo.category.length > 0) {
      items = items.filter((item) => poisFilteredInfo.category?.includes(item.category));
    }

    return items;
  }, [poisData?.items, poisSearchText, poisFilteredInfo]);

  // Filtered and searched Issues items
  const filteredIssuesItems = useMemo(() => {
    if (!issuesData?.items) return [];
    let items = [...issuesData.items];

    // Apply search filter (by title, description, area, ID, reporter name)
    if (issuesSearchText.trim()) {
      const needle = normalizeGreekSearch(issuesSearchText.trim());
      items = items.filter((item) => {
        const title = normalizeGreekSearch(item.title || '');
        const description = normalizeGreekSearch(item.description || '');
        const area = normalizeGreekSearch(item.area || '');
        const issueId = normalizeGreekSearch(formatIssueId(item.id));
        const reporterName = normalizeGreekSearch(`${item.reportedBy.firstName} ${item.reportedBy.lastName}`);
        return (
          title.includes(needle) ||
          description.includes(needle) ||
          area.includes(needle) ||
          issueId.includes(needle) ||
          reporterName.includes(needle)
        );
      });
    }

    // Apply status filter
    if (issuesFilteredInfo.status && issuesFilteredInfo.status.length > 0) {
      items = items.filter((item) => issuesFilteredInfo.status?.includes(item.status));
    }

    // Apply priority filter
    if (issuesFilteredInfo.priority && issuesFilteredInfo.priority.length > 0) {
      items = items.filter((item) => issuesFilteredInfo.priority?.includes(item.priority));
    }

    // Apply category filter
    if (issuesFilteredInfo.category && issuesFilteredInfo.category.length > 0) {
      items = items.filter((item) => issuesFilteredInfo.category?.includes(item.category));
    }

    return items;
  }, [issuesData?.items, issuesSearchText, issuesFilteredInfo]);

  // Filtered and searched Lost & Found items
  const filteredLostAndFoundItems = useMemo(() => {
    if (!lostAndFoundData?.items) return [];
    let items = [...lostAndFoundData.items];

    // Apply search filter (by title, description)
    if (lostAndFoundSearchText.trim()) {
      const needle = normalizeGreekSearch(lostAndFoundSearchText.trim());
      items = items.filter((item) => {
        const title = normalizeGreekSearch(item.title || '');
        const description = normalizeGreekSearch(item.description || '');
        return title.includes(needle) || description.includes(needle);
      });
    }

    // Apply status filter
    if (lostAndFoundFilteredInfo.status && lostAndFoundFilteredInfo.status.length > 0) {
      items = items.filter((item) => lostAndFoundFilteredInfo.status?.includes(item.status));
    }

    // Apply type filter
    if (lostAndFoundFilteredInfo.type && lostAndFoundFilteredInfo.type.length > 0) {
      items = items.filter((item) => lostAndFoundFilteredInfo.type?.includes(item.type));
    }

    // Apply category filter
    if (lostAndFoundFilteredInfo.category && lostAndFoundFilteredInfo.category.length > 0) {
      items = items.filter((item) => lostAndFoundFilteredInfo.category?.includes(item.category));
    }

    return items;
  }, [lostAndFoundData?.items, lostAndFoundSearchText, lostAndFoundFilteredInfo]);

  // Filtered and searched Volunteering items
  const filteredVolunteeringItems = useMemo(() => {
    if (!volunteeringData?.items) return [];
    let items = [...volunteeringData.items];

    // Apply search filter (by title)
    if (volunteeringSearchText.trim()) {
      const needle = normalizeGreekSearch(volunteeringSearchText.trim());
      items = items.filter((item) => {
        const title = getVolunteeringTitle(item);
        return normalizeGreekSearch(title).includes(needle);
      });
    }

    // Apply status filter
    if (volunteeringFilteredInfo.status && volunteeringFilteredInfo.status.length > 0) {
      items = items.filter((item) => volunteeringFilteredInfo.status?.includes(item.status));
    }

    return items;
  }, [volunteeringData?.items, volunteeringSearchText, volunteeringFilteredInfo]);

  // News table columns
  const newsColumns: ColumnsType<NewsArticle> = [
    {
      title: t('news.columns.title', 'Title'),
      dataIndex: 'translations',
      key: 'title',
      width: 200,
      render: (_translations: NewsArticle['translations'], record: NewsArticle) => {
        const title = getNewsTitle(record);
        return (
          <div style={{ 
            fontWeight: 500,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.4',
            wordBreak: 'break-word'
          }}>
            {title}
          </div>
        );
      },
    },
    {
      title: t('news.columns.category', 'Category'),
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (category: NewsCategory) => (
        <Tag color={getNewsCategoryColor(category)}>
          {getNewsCategoryLabel(category)}
        </Tag>
      ),
      filters: newsData?.items ? Array.from(new Set(newsData.items.map((n) => n.category))).map((category) => ({
        text: getNewsCategoryLabel(category),
        value: category,
      })) : [],
      filteredValue: newsFilteredInfo.category || null,
      onFilter: (value: React.Key | boolean, record: NewsArticle) => record.category === value,
    },
    {
      title: t('news.columns.author', 'Author'),
      dataIndex: 'authorName',
      key: 'authorName',
      width: 150,
      ellipsis: true,
    },
    {
      title: t('news.columns.publishDate', 'Publish date'),
      dataIndex: 'publishDate',
      key: 'publishDate',
      width: 120,
      render: (date: Date | undefined) => date ? formatDateShort(date) : '-',
      sorter: (a, b) => {
        const aDate = a.publishDate?.getTime() || 0;
        const bDate = b.publishDate?.getTime() || 0;
        return aDate - bDate;
      },
    },
    {
      title: t('news.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: NewsStatus) => (
        <Tag color={getNewsStatusColor(status)}>
          {getNewsStatusLabel(status)}
        </Tag>
      ),
      filters: newsData?.items ? Array.from(new Set(newsData.items.map((n) => n.status))).map((status) => ({
        text: getNewsStatusLabel(status),
        value: status,
      })) : [],
      filteredValue: newsFilteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: NewsArticle) => record.status === value,
    },
    {
      title: t('news.columns.views', 'Views'),
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: 90,
      align: 'center' as const,
      render: (count: number) => count || 0,
      sorter: (a, b) => (a.viewCount || 0) - (b.viewCount || 0),
    },
  ];

  // News table change handler
  const handleNewsTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<NewsArticle> | SorterResult<NewsArticle>[]
  ) => {
    const normalizedFilters: Record<string, React.Key[] | null> = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (!value) {
        normalizedFilters[key] = null;
      } else if (Array.isArray(value)) {
        normalizedFilters[key] = value.filter((v): v is React.Key => typeof v !== 'boolean');
      } else if (typeof value === 'string' || typeof value === 'number') {
        normalizedFilters[key] = [value];
      } else {
        normalizedFilters[key] = null;
      }
    });
    setNewsFilteredInfo(normalizedFilters);
  };

  // Staff users table columns
  const staffColumns = [
    {
      title: t('userManagement.staffUsers.columns.name', 'Name'),
      key: 'name',
      render: (user: AllUser) => getUserDisplayName(user),
    },
    {
      title: t('userManagement.staffUsers.columns.email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => <Text copyable>{email}</Text>,
    },
    {
      title: t('userManagement.staffUsers.columns.role', 'Role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={getRoleColor(role)}>
          {getRoleLabel(role)}
        </Tag>
      ),
    },
    {
      title: t('userManagement.staffUsers.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {formatStatus(status)}
        </Tag>
      ),
    },
    {
      title: t('userManagement.staffUsers.columns.lastLogin', 'Last login'),
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (lastLogin: Date | string | undefined) => formatDate(lastLogin),
    },
    {
      title: t('userManagement.staffUsers.columns.createdAt', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: Date | string | undefined) => formatDate(createdAt),
    },
  ];


  if (municipalityLoading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (municipalityError) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error Loading Municipality"
          description={municipalityError}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/municipalities')}>
              Back to Municipalities
            </Button>
          }
        />
      </div>
    );
  }

  if (!municipality) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Municipality Not Found"
          description="The municipality you're looking for doesn't exist or has been removed."
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/municipalities')}>
              Back to Municipalities
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header Section */}
      <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/municipalities')}
            type="text"
          >
            Back
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {municipality.name}
          </Title>
          <Tag 
            color={getStatusColor(municipality.status)} 
            icon={getStatusIcon(municipality.status)}
            style={{ fontSize: '14px', padding: '4px 12px' }}
          >
            {formatStatus(municipality.status)}
          </Tag>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="News"
                value={statsLoading ? 0 : statistics.news}
                prefix={<FileTextOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="Events"
                value={statsLoading ? 0 : statistics.events}
                prefix={<EventOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="Citizens"
                value={statsLoading ? 0 : statistics.citizens}
                prefix={<TeamOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="Issues"
                value={statsLoading ? 0 : statistics.issues}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="Surveys"
                value={statsLoading ? 0 : statistics.surveys}
                prefix={<FormOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="Lost and Found"
                value={statsLoading ? 0 : statistics.lostAndFound}
                prefix={<SearchOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <StatisticsCard>
              <Statistic
                title="POI"
                value={statsLoading ? 0 : statistics.poi}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              />
            </StatisticsCard>
          </Col>
        </Row>
      </Space>

      {/* Tabs Section */}
      {isMobile ? (
        <Card style={{ marginBottom: '24px', borderRadius: '12px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text strong>Section:</Text>
            <Select
              value={activeTab}
              onChange={setActiveTab}
              style={{ width: '100%' }}
              size="large"
            >
              <Select.Option value="details">{t('tabs.details', 'Details')}</Select.Option>
              <Select.Option value="content">{t('tabs.content', 'Content')}</Select.Option>
              <Select.Option value="services">{t('tabs.services', 'Services')}</Select.Option>
              <Select.Option value="governance">{t('tabs.governance', 'Governance')}</Select.Option>
              <Select.Option value="settings">{t('tabs.settings', 'Settings')}</Select.Option>
            </Select>
          </Space>
        </Card>
      ) : null}

      <Card
        style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type={isMobile ? 'line' : 'card'}
          items={[
            {
              key: 'details',
              label: t('tabs.details', 'Details'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* Basic Information Card */}
                  <InfoCard
                    title={
                      <Space>
                        <BankOutlined />
                        Basic information
                      </Space>
                    }
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} lg={12}>
                        <InfoRow>
                          <InfoLabel>Name</InfoLabel>
                          <InfoValue>{municipality.name}</InfoValue>
                        </InfoRow>
                        
                        <InfoRow>
                          <InfoLabel>
                            <MailOutlined style={{ marginRight: '8px' }} />
                            Email
                          </InfoLabel>
                          <InfoValue copyable>{municipality.email}</InfoValue>
                        </InfoRow>
                        
                        <InfoRow>
                          <InfoLabel>
                            <GlobalOutlined style={{ marginRight: '8px' }} />
                            Country
                          </InfoLabel>
                          <InfoValue>{municipality.country}</InfoValue>
                        </InfoRow>
                        
                        {municipality.phone && (
                          <InfoRow>
                            <InfoLabel>
                              <PhoneOutlined style={{ marginRight: '8px' }} />
                              Phone
                            </InfoLabel>
                            <InfoValue>{municipality.phone}</InfoValue>
                          </InfoRow>
                        )}
                      </Col>

                      <Col xs={24} lg={12}>
                        <InfoRow>
                          <InfoLabel>
                            <CalendarOutlined style={{ marginRight: '8px' }} />
                            Created
                          </InfoLabel>
                          <InfoValue>{formatDate(municipality.createdAt)}</InfoValue>
                        </InfoRow>
                        
                        <InfoRow>
                          <InfoLabel>
                            <CalendarOutlined style={{ marginRight: '8px' }} />
                            Last updated
                          </InfoLabel>
                          <InfoValue>{formatDate(municipality.updatedAt)}</InfoValue>
                        </InfoRow>
                        
                        {municipality.invitationSentAt && (
                          <InfoRow>
                            <InfoLabel>
                              <MailOutlined style={{ marginRight: '8px' }} />
                              Invitation sent
                            </InfoLabel>
                            <InfoValue>{formatDate(municipality.invitationSentAt)}</InfoValue>
                          </InfoRow>
                        )}
                      </Col>
                    </Row>
                  </InfoCard>

                  {/* User Management Section */}
                  <UserManagementCard
                    title={
                      <Space>
                        <TeamOutlined />
                        {t('userManagement.title', 'User management')}
                      </Space>
                    }
                  >
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      {/* Staff Users Section */}
                      <Card
                        type="inner"
                        title={
                          <Space>
                            <UserOutlined />
                            {t('userManagement.staffUsers.title', 'Municipality staff')}
                          </Space>
                        }
                        extra={
                          <Statistic
                            value={staffUsers.length}
                            prefix={<UserOutlined />}
                            valueStyle={{ fontSize: '18px', fontWeight: 'bold' }}
                          />
                        }
                      >
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                          <Col span={8}>
                            <Statistic
                              title={t('userManagement.staffUsers.active', 'Active')}
                              value={staffUsers.filter(u => u.status === 'active').length}
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic
                              title={t('userManagement.staffUsers.inactive', 'Inactive')}
                              value={staffUsers.filter(u => u.status === 'inactive').length}
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                          <Col span={8}>
                            <Statistic
                              title={t('userManagement.staffUsers.pending', 'Pending')}
                              value={staffUsers.filter(u => u.status === 'pending').length}
                              valueStyle={{ fontSize: '16px' }}
                            />
                          </Col>
                        </Row>
                        
                        {usersLoading ? (
                          <div style={{ textAlign: 'center', padding: '24px' }}>
                            <Spin />
                          </div>
                        ) : staffUsers.length === 0 ? (
                          <Empty
                            description={t('userManagement.staffUsers.noUsers', 'No staff users found')}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        ) : (
                          <Table
                            dataSource={staffUsers}
                            columns={staffColumns}
                            rowKey="id"
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            size="small"
                            scroll={{ x: 'max-content' }}
                          />
                        )}
                      </Card>
                    </Space>
                  </UserManagementCard>
                </Space>
              ),
            },
            {
              key: 'news',
              label: t('tabs.news', 'News'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* News Articles Section */}
                  <Card
                    title={
                      <Space>
                        <FileTextOutlined />
                        <span>{t('news.title', 'News articles')}</span>
                      </Space>
                    }
                    extra={
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        {t('news.subtitle', 'Manage and view all news articles published by this municipality')}
                      </Text>
                    }
                    style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                  >
                    {newsLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : newsError ? (
                      <Alert
                        message={t('news.loadError', 'Failed to load news articles')}
                        type="error"
                        showIcon
                      />
                    ) : newsData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* News Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.total', 'Total news')}
                                value={newsData.statistics.total}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.published', 'Published')}
                                value={newsData.statistics.published}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.draft', 'Draft')}
                                value={newsData.statistics.draft}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.archived', 'Archived')}
                                value={newsData.statistics.archived}
                                prefix={<CloseCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.totalViews', 'Total views')}
                                value={newsData.statistics.totalViews}
                                prefix={<UserOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.averageViews', 'Average views')}
                                value={newsData.statistics.averageViews}
                                prefix={<UserOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.pinned', 'Pinned')}
                                value={newsData.statistics.pinned}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('news.statistics.featured', 'Featured')}
                                value={newsData.statistics.featured}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* News Articles Table */}
                        <Card
                          title={t('news.title', 'News articles')}
                          style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* Search Input */}
                            <Input
                              placeholder={t('news.searchPlaceholder', 'Search by title...')}
                              prefix={<SearchOutlined />}
                              value={newsSearchText}
                              onChange={(e) => setNewsSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredNewsItems.length === 0 ? (
                              <Empty
                                description={t('news.noNews', 'No news articles found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={newsColumns}
                                dataSource={filteredNewsItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handleNewsTableChange}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('news.noNews', 'No news articles found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'events',
              label: t('tabs.events', 'Events'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* Events Section */}
                  <Card
                    title={
                      <Space>
                        <EventOutlined />
                        <span>{t('events.title', 'Events')}</span>
                      </Space>
                    }
                    extra={
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        {t('events.subtitle', 'Manage and view all events organized by this municipality')}
                      </Text>
                    }
                    style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                  >
                    {eventsLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : eventsError ? (
                      <Alert
                        message={t('events.loadError', 'Failed to load events')}
                        type="error"
                        showIcon
                      />
                    ) : eventsData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Events Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('events.statistics.total', 'Total events')}
                                value={eventsData.statistics.total}
                                prefix={<EventOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('events.statistics.upcoming', 'Upcoming')}
                                value={eventsData.statistics.upcoming}
                                prefix={<CalendarOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('events.statistics.ongoing', 'Ongoing')}
                                value={eventsData.statistics.ongoing}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('events.statistics.past', 'Past')}
                                value={eventsData.statistics.past}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('events.statistics.totalRegistrations', 'Total registrations')}
                                value={eventsData.statistics.totalRegistrations}
                                prefix={<UserOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('events.statistics.averageRegistrations', 'Average registrations')}
                                value={eventsData.statistics.averageRegistrations}
                                prefix={<UserOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* Events Table */}
                        <Card
                          title={t('events.title', 'Events')}
                          style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            {/* Search Input */}
                            <Input
                              placeholder={t('events.searchPlaceholder', 'Search by title...')}
                              prefix={<SearchOutlined />}
                              value={eventsSearchText}
                              onChange={(e) => setEventsSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredEventsItems.length === 0 ? (
                              <Empty
                                description={t('events.noEvents', 'No events found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={eventsColumns}
                                dataSource={filteredEventsItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handleEventsTableChange}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('events.noEvents', 'No events found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'surveys',
              label: t('tabs.surveys', 'Surveys'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Card>
                    {surveysLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : surveysError ? (
                      <Alert
                        message={t('surveys.loadError', 'Error loading surveys')}
                        description={surveysError}
                        type="error"
                        showIcon
                      />
                    ) : surveysData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Surveys Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('surveys.statistics.total', 'Total surveys')}
                                value={surveysData.statistics.total}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('surveys.statistics.active', 'Active')}
                                value={surveysData.statistics.active}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('surveys.statistics.draft', 'Draft')}
                                value={surveysData.statistics.draft}
                                prefix={<EditOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('surveys.statistics.closed', 'Closed')}
                                value={surveysData.statistics.closed}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('surveys.statistics.totalResponses', 'Total responses')}
                                value={surveysData.statistics.totalResponses}
                                prefix={<UserOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('surveys.statistics.averageResponseRate', 'Avg responses')}
                                value={surveysData.statistics.averageResponseRate}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* Surveys Table */}
                        <Card
                          title={
                            <Space>
                              <FileTextOutlined />
                              {t('surveys.title', 'Surveys')}
                            </Space>
                          }
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Input
                              placeholder={t('surveys.searchPlaceholder', 'Search by title...')}
                              prefix={<SearchOutlined />}
                              value={surveysSearchText}
                              onChange={(e) => setSurveysSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredSurveysItems.length === 0 ? (
                              <Empty
                                description={t('surveys.noSurveys', 'No surveys found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={surveysColumns}
                                dataSource={filteredSurveysItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handleSurveysTableChange}
                                loading={surveysLoading}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('surveys.noSurveys', 'No surveys found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'poi',
              label: t('tabs.poi', 'POI'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Card>
                    {poisLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : poisError ? (
                      <Alert
                        message={t('poi.loadError', 'Error loading POIs')}
                        description={poisError}
                        type="error"
                        showIcon
                      />
                    ) : poisData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* POI Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('poi.statistics.total', 'Total POIs')}
                                value={poisData.statistics.total}
                                prefix={<EnvironmentOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('poi.statistics.published', 'Published')}
                                value={poisData.statistics.published}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('poi.statistics.draft', 'Draft')}
                                value={poisData.statistics.draft}
                                prefix={<EditOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('poi.statistics.archived', 'Archived')}
                                value={poisData.statistics.archived}
                                prefix={<CloseCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('poi.statistics.totalViews', 'Total views')}
                                value={poisData.statistics.totalViews}
                                prefix={<EyeOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('poi.statistics.averageViews', 'Average views')}
                                value={poisData.statistics.averageViews}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* POI Table */}
                        <Card
                          title={
                            <Space>
                              <EnvironmentOutlined />
                              {t('poi.title', 'Points of Interest')}
                            </Space>
                          }
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Input
                              placeholder={t('poi.searchPlaceholder', 'Search by title or address...')}
                              prefix={<SearchOutlined />}
                              value={poisSearchText}
                              onChange={(e) => setPoisSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredPOIsItems.length === 0 ? (
                              <Empty
                                description={t('poi.noPOIs', 'No POIs found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={poisColumns}
                                dataSource={filteredPOIsItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handlePOIsTableChange}
                                loading={poisLoading}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('poi.noPOIs', 'No POIs found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'issues',
              label: t('tabs.issues', 'Issues'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Card>
                    {issuesLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : issuesError ? (
                      <Alert
                        message={t('issues.loadError', 'Error loading issues')}
                        description={issuesError}
                        type="error"
                        showIcon
                      />
                    ) : issuesData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Issues Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.total', 'Total issues')}
                                value={issuesData.statistics.total}
                                prefix={<ExclamationCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.reported', 'Reported')}
                                value={issuesData.statistics.reported}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.inProgress', 'In Progress')}
                                value={issuesData.statistics.inProgress}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.resolved', 'Resolved')}
                                value={issuesData.statistics.resolved}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.overdue', 'Overdue')}
                                value={issuesData.statistics.overdue}
                                prefix={<CloseCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.urgent', 'Urgent')}
                                value={issuesData.statistics.urgent}
                                prefix={<ExclamationCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('issues.statistics.averageResolutionTime', 'Avg resolution time')}
                                value={issuesData.statistics.averageResolutionTime}
                                suffix={t('issues.hours', 'h')}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* Issues Table */}
                        <Card
                          title={
                            <Space>
                              <ExclamationCircleOutlined />
                              {t('issues.title', 'Issues')}
                            </Space>
                          }
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Input
                              placeholder={t('issues.searchPlaceholder', 'Search by title, ID, or reporter...')}
                              prefix={<SearchOutlined />}
                              value={issuesSearchText}
                              onChange={(e) => setIssuesSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredIssuesItems.length === 0 ? (
                              <Empty
                                description={t('issues.noIssues', 'No issues found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={issuesColumns}
                                dataSource={filteredIssuesItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handleIssuesTableChange}
                                loading={issuesLoading}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('issues.noIssues', 'No issues found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'lostAndFound',
              label: t('tabs.lostAndFound', 'Lost & Found'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Card>
                    {lostAndFoundLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : lostAndFoundError ? (
                      <Alert
                        message={t('lostAndFound.loadError', 'Error loading lost & found')}
                        description={lostAndFoundError}
                        type="error"
                        showIcon
                      />
                    ) : lostAndFoundData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Lost & Found Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.total', 'Total records')}
                                value={lostAndFoundData.statistics.total}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.pending', 'Pending')}
                                value={lostAndFoundData.statistics.pending}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.published', 'Published')}
                                value={lostAndFoundData.statistics.published}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.resolved', 'Resolved')}
                                value={lostAndFoundData.statistics.resolved}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.lost', 'Lost')}
                                value={lostAndFoundData.statistics.lost}
                                prefix={<ExclamationCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.found', 'Found')}
                                value={lostAndFoundData.statistics.found}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.totalViews', 'Total views')}
                                value={lostAndFoundData.statistics.totalViews}
                                prefix={<EyeOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('lostAndFound.statistics.averageViews', 'Average views')}
                                value={lostAndFoundData.statistics.averageViews}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* Lost & Found Table */}
                        <Card
                          title={
                            <Space>
                              <FileTextOutlined />
                              {t('lostAndFound.title', 'Lost & Found')}
                            </Space>
                          }
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Input
                              placeholder={t('lostAndFound.searchPlaceholder', 'Search by title...')}
                              prefix={<SearchOutlined />}
                              value={lostAndFoundSearchText}
                              onChange={(e) => setLostAndFoundSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredLostAndFoundItems.length === 0 ? (
                              <Empty
                                description={t('lostAndFound.noRecords', 'No records found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={lostAndFoundColumns}
                                dataSource={filteredLostAndFoundItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handleLostAndFoundTableChange}
                                loading={lostAndFoundLoading}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('lostAndFound.noRecords', 'No records found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'volunteering',
              label: t('tabs.volunteering', 'Volunteering'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Card>
                    {volunteeringLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : volunteeringError ? (
                      <Alert
                        message={t('volunteering.loadError', 'Error loading volunteering activities')}
                        description={volunteeringError}
                        type="error"
                        showIcon
                      />
                    ) : volunteeringData ? (
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Volunteering Statistics Cards */}
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('volunteering.statistics.total', 'Total activities')}
                                value={volunteeringData.statistics.total}
                                prefix={<TeamOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('volunteering.statistics.active', 'Active')}
                                value={volunteeringData.statistics.active}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('volunteering.statistics.upcoming', 'Upcoming')}
                                value={volunteeringData.statistics.upcoming}
                                prefix={<CalendarOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('volunteering.statistics.completed', 'Completed')}
                                value={volunteeringData.statistics.completed}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('volunteering.statistics.totalRegistrations', 'Total registrations')}
                                value={volunteeringData.statistics.totalRegistrations}
                                prefix={<UserOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                          <Col xs={12} sm={8} md={6}>
                            <StatisticsCard>
                              <Statistic
                                title={t('volunteering.statistics.averageRegistrations', 'Avg registrations')}
                                value={volunteeringData.statistics.averageRegistrations}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                              />
                            </StatisticsCard>
                          </Col>
                        </Row>

                        {/* Volunteering Table */}
                        <Card
                          title={
                            <Space>
                              <TeamOutlined />
                              {t('volunteering.title', 'Volunteering Activities')}
                            </Space>
                          }
                        >
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Input
                              placeholder={t('volunteering.searchPlaceholder', 'Search by title...')}
                              prefix={<SearchOutlined />}
                              value={volunteeringSearchText}
                              onChange={(e) => setVolunteeringSearchText(e.target.value)}
                              allowClear
                              style={{ maxWidth: '400px' }}
                            />
                            
                            {filteredVolunteeringItems.length === 0 ? (
                              <Empty
                                description={t('volunteering.noActivities', 'No activities found')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={volunteeringColumns}
                                dataSource={filteredVolunteeringItems}
                                rowKey="id"
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                size="small"
                                onChange={handleVolunteeringTableChange}
                                loading={volunteeringLoading}
                              />
                            )}
                          </Space>
                        </Card>
                      </Space>
                    ) : (
                      <Empty
                        description={t('volunteering.noActivities', 'No activities found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'governance',
              label: t('tabs.governance', 'Governance'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* Mayor Section */}
                  <Card
                    title={
                      <Space>
                        <UserOutlined />
                        {t('governance.mayor.title', 'Mayor')}
                      </Space>
                    }
                  >
                    {mayorLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : mayorError ? (
                      <Alert
                        message={t('governance.mayor.loadError', 'Error loading mayor')}
                        description={mayorError}
                        type="error"
                        showIcon
                      />
                    ) : mayorData ? (
                      <Row gutter={[24, 24]}>
                        <Col xs={24} md={8}>
                          {mayorData.profileImage ? (
                            <img
                              src={mayorData.profileImage}
                              alt={getMayorName(mayorData)}
                              style={{
                                width: '100%',
                                maxWidth: '200px',
                                borderRadius: '8px',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Avatar size={200} icon={<UserOutlined />} />
                          )}
                        </Col>
                        <Col xs={24} md={16}>
                          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div>
                              <Text strong style={{ fontSize: '20px' }}>
                                {getMayorName(mayorData)}
                              </Text>
                            </div>
                            {mayorData.email && (
                              <div>
                                <MailOutlined style={{ marginRight: '8px' }} />
                                <Text copyable>{mayorData.email}</Text>
                              </div>
                            )}
                            {mayorData.welcomeMessage && (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHTML(mayorData.welcomeMessage),
                                }}
                                style={{
                                  lineHeight: '1.6',
                                  color: 'rgba(0, 0, 0, 0.85)',
                                }}
                              />
                            )}
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {t('governance.mayor.createdAt', 'Created')}: {formatDateShort(mayorData.createdAt)}
                              </Text>
                            </div>
                          </Space>
                        </Col>
                      </Row>
                    ) : (
                      <Empty
                        description={t('governance.mayor.noMayor', 'No mayor information available')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* Municipal Council Section */}
                  <Card
                    title={
                      <Space>
                        <TeamOutlined />
                        {t('governance.council.title', 'Municipal Council')}
                        {councilData && (
                          <Tag color="blue">{councilData.total}</Tag>
                        )}
                      </Space>
                    }
                  >
                    {councilLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : councilError ? (
                      <Alert
                        message={t('governance.council.loadError', 'Error loading councilors')}
                        description={councilError}
                        type="error"
                        showIcon
                      />
                    ) : councilData && councilData.items.length > 0 ? (
                      <Table
                        columns={councilColumns}
                        dataSource={councilData.items}
                        rowKey="id"
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        size="small"
                      />
                    ) : (
                      <Empty
                        description={t('governance.council.noCouncilors', 'No councilors found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* Committees Section */}
                  <Card
                    title={
                      <Space>
                        <BankOutlined />
                        {t('governance.committees.title', 'Committees')}
                        {committeesData && (
                          <Tag color="blue">{committeesData.total}</Tag>
                        )}
                      </Space>
                    }
                  >
                    {committeesLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : committeesError ? (
                      <Alert
                        message={t('governance.committees.loadError', 'Error loading committees')}
                        description={committeesError}
                        type="error"
                        showIcon
                      />
                    ) : committeesData && committeesData.items.length > 0 ? (
                      <Table
                        columns={committeesColumns}
                        dataSource={committeesData.items}
                        rowKey="id"
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        size="small"
                      />
                    ) : (
                      <Empty
                        description={t('governance.committees.noCommittees', 'No committees found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* Committee Members Section */}
                  <Card
                    title={
                      <Space>
                        <UserOutlined />
                        {t('governance.committeeMembers.title', 'Committee Members')}
                        {committeeMembersData && (
                          <Tag color="blue">{committeeMembersData.total}</Tag>
                        )}
                      </Space>
                    }
                  >
                    {committeeMembersLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : committeeMembersError ? (
                      <Alert
                        message={t('governance.committeeMembers.loadError', 'Error loading committee members')}
                        description={committeeMembersError}
                        type="error"
                        showIcon
                      />
                    ) : committeeMembersData && committeeMembersData.items.length > 0 ? (
                      <Table
                        columns={committeeMembersColumns}
                        dataSource={committeeMembersData.items}
                        rowKey="id"
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                        size="small"
                      />
                    ) : (
                      <Empty
                        description={t('governance.committeeMembers.noMembers', 'No committee members found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
            {
              key: 'settings',
              label: t('tabs.settings', 'Settings'),
              children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* 1. Basic Municipality Information */}
                  <Card
                    title={
                      <Space>
                        <BankOutlined />
                        {t('settings.basicInfo.title', 'Basic Municipality Information')}
                      </Space>
                    }
                  >
                    {municipalityLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : municipalityError ? (
                      <Alert
                        message={t('settings.basicInfo.loadError', 'Error loading municipality information')}
                        description={municipalityError}
                        type="error"
                        showIcon
                      />
                    ) : municipality ? (
                      <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.displayId', 'Display ID')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text strong>{municipality.displayId || '-'}</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.firestoreId', 'Firestore ID')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text strong copyable>{municipality.id}</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.name', 'Name')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text strong>{municipality.name}</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.country', 'Country')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text strong>{municipality.country}</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.status', 'Status')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Select
                              value={municipality.status}
                              onChange={handleStatusChange}
                              loading={updatingStatus}
                              disabled={updatingStatus}
                              style={{ width: '100%' }}
                            >
                              <Select.Option value="trial">
                                <Space>
                                  <Tag color="orange">{t('status.trial', 'Trial')}</Tag>
                                  <span style={{ fontSize: '12px', color: '#666' }}>
                                    {t('settings.status.trialHint', 'Hidden from citizens')}
                                  </span>
                                </Space>
                              </Select.Option>
                              <Select.Option value="provisional">
                                <Space>
                                  <Tag color="blue">{t('status.provisional', 'Provisional')}</Tag>
                                  <span style={{ fontSize: '12px', color: '#666' }}>
                                    {t('settings.status.provisionalHint', 'Hidden from citizens')}
                                  </span>
                                </Space>
                              </Select.Option>
                              <Select.Option value="active">
                                <Space>
                                  <Tag color="green">{t('status.active', 'Active')}</Tag>
                                  <span style={{ fontSize: '12px', color: '#666' }}>
                                    {t('settings.status.activeHint', 'Visible to citizens')}
                                  </span>
                                </Space>
                              </Select.Option>
                            </Select>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.createdAt', 'Created')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text>{formatDateShort(new Date(municipality.createdAt))}</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Text type="secondary">{t('settings.basicInfo.updatedAt', 'Updated')}</Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text>{formatDateShort(new Date(municipality.updatedAt))}</Text>
                          </div>
                        </Col>
                        {municipality.invitationSentAt && (
                          <Col xs={24} sm={12} md={8}>
                            <Text type="secondary">{t('settings.basicInfo.invitationSentAt', 'Invitation Sent')}</Text>
                            <div style={{ marginTop: '4px' }}>
                              <Text>{formatDateShort(new Date(municipality.invitationSentAt))}</Text>
                            </div>
                          </Col>
                        )}
                      </Row>
                    ) : (
                      <Empty
                        description={t('settings.basicInfo.noData', 'No municipality information available')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* 2. Contact Information */}
                  <Card
                    title={
                      <Space>
                        <MailOutlined />
                        {t('settings.contact.title', 'Contact Information')}
                      </Space>
                    }
                  >
                    {municipalityLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : municipality ? (
                      <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                          <Space>
                            <MailOutlined />
                            <Text type="secondary">{t('settings.contact.email', 'Email')}</Text>
                          </Space>
                          <div style={{ marginTop: '4px' }}>
                            <Text copyable>{municipality.email || '-'}</Text>
                          </div>
                        </Col>
                        {municipality.contactEmail && municipality.contactEmail !== municipality.email && (
                          <Col xs={24} sm={12} md={8}>
                            <Space>
                              <MailOutlined />
                              <Text type="secondary">{t('settings.contact.contactEmail', 'Contact Email')}</Text>
                            </Space>
                            <div style={{ marginTop: '4px' }}>
                              <Text copyable>{municipality.contactEmail}</Text>
                            </div>
                          </Col>
                        )}
                        {municipality.phone && (
                          <Col xs={24} sm={12} md={8}>
                            <Space>
                              <PhoneOutlined />
                              <Text type="secondary">{t('settings.contact.phone', 'Phone')}</Text>
                            </Space>
                            <div style={{ marginTop: '4px' }}>
                              <Text copyable>{municipality.phone}</Text>
                            </div>
                          </Col>
                        )}
                        {municipality.contactPhone && (
                          <Col xs={24} sm={12} md={8}>
                            <Space>
                              <PhoneOutlined />
                              <Text type="secondary">{t('settings.contact.contactPhone', 'Contact Phone')}</Text>
                            </Space>
                            <div style={{ marginTop: '4px' }}>
                              <Text copyable>{municipality.contactPhone}</Text>
                            </div>
                          </Col>
                        )}
                        {municipality.address && (
                          <Col xs={24} sm={12} md={8}>
                            <Space>
                              <EnvironmentOutlined />
                              <Text type="secondary">{t('settings.contact.address', 'Address')}</Text>
                            </Space>
                            <div style={{ marginTop: '4px' }}>
                              <Text>{municipality.address}</Text>
                            </div>
                          </Col>
                        )}
                        {municipality.website && (
                          <Col xs={24} sm={12} md={8}>
                            <Space>
                              <GlobalOutlined />
                              <Text type="secondary">{t('settings.contact.website', 'Website')}</Text>
                            </Space>
                            <div style={{ marginTop: '4px' }}>
                              <Text>
                                <a href={municipality.website} target="_blank" rel="noopener noreferrer">
                                  {municipality.website}
                                </a>
                              </Text>
                            </div>
                          </Col>
                        )}
                      </Row>
                    ) : (
                      <Empty
                        description={t('settings.contact.noData', 'No contact information available')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* 3. Media Assets */}
                  <Card
                    title={
                      <Space>
                        <FileTextOutlined />
                        {t('settings.media.title', 'Media Assets')}
                      </Space>
                    }
                  >
                    {municipalityLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : municipality ? (
                      <Row gutter={[24, 16]}>
                        {municipality.logoUrl && (
                          <Col xs={24} md={12}>
                            <Text type="secondary">{t('settings.media.logo', 'Logo')}</Text>
                            <div style={{ marginTop: '8px' }}>
                              <img
                                src={municipality.logoUrl}
                                alt="Municipality Logo"
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  objectFit: 'contain',
                                  border: '1px solid #d9d9d9',
                                  padding: '8px',
                                }}
                              />
                            </div>
                            <div style={{ marginTop: '4px' }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                <a href={municipality.logoUrl} target="_blank" rel="noopener noreferrer">
                                  {t('settings.media.viewFullSize', 'View full size')}
                                </a>
                              </Text>
                            </div>
                          </Col>
                        )}
                        {municipality.coverImageUrl && (
                          <Col xs={24} md={12}>
                            <Text type="secondary">{t('settings.media.coverImage', 'Cover Image')}</Text>
                            <div style={{ marginTop: '8px' }}>
                              <img
                                src={municipality.coverImageUrl}
                                alt="Municipality Cover"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  border: '1px solid #d9d9d9',
                                }}
                              />
                            </div>
                            <div style={{ marginTop: '4px' }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                <a href={municipality.coverImageUrl} target="_blank" rel="noopener noreferrer">
                                  {t('settings.media.viewFullSize', 'View full size')}
                                </a>
                              </Text>
                            </div>
                          </Col>
                        )}
                        {!municipality.logoUrl && !municipality.coverImageUrl && (
                          <Col xs={24}>
                            <Empty
                              description={t('settings.media.noMedia', 'No media assets available')}
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                          </Col>
                        )}
                      </Row>
                    ) : (
                      <Empty
                        description={t('settings.media.noData', 'No media information available')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* 4. Localization Settings */}
                  <Card
                    title={
                      <Space>
                        <GlobalOutlined />
                        {t('settings.localization.title', 'Localization Settings')}
                      </Space>
                    }
                  >
                    {municipalityLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : municipality ? (
                      <Row gutter={[24, 16]}>
                        {municipality.primaryLanguage && (
                          <Col xs={24} sm={12} md={8}>
                            <Text type="secondary">{t('settings.localization.primaryLanguage', 'Primary Language')}</Text>
                            <div style={{ marginTop: '4px' }}>
                              <Tag color="blue">
                                {municipality.primaryLanguage === 'en' ? 'English' : municipality.primaryLanguage === 'el' ? 'Greek' : municipality.primaryLanguage}
                              </Tag>
                            </div>
                          </Col>
                        )}
                        {municipality.availableLanguages && municipality.availableLanguages.length > 0 && (
                          <Col xs={24} sm={12} md={8}>
                            <Text type="secondary">{t('settings.localization.availableLanguages', 'Available Languages')}</Text>
                            <div style={{ marginTop: '4px' }}>
                              <Space wrap>
                                {municipality.availableLanguages.map((lang) => (
                                  <Tag key={lang} color="cyan">
                                    {lang === 'en' ? 'English' : lang === 'el' ? 'Greek' : lang}
                                  </Tag>
                                ))}
                              </Space>
                            </div>
                          </Col>
                        )}
                        {municipality.timezone && (
                          <Col xs={24} sm={12} md={8}>
                            <Text type="secondary">{t('settings.localization.timezone', 'Timezone')}</Text>
                            <div style={{ marginTop: '4px' }}>
                              <Text>{municipality.timezone}</Text>
                            </div>
                          </Col>
                        )}
                        {municipality.description && (
                          <Col xs={24}>
                            <Text type="secondary">{t('settings.localization.description', 'Description')}</Text>
                            <div style={{ marginTop: '4px' }}>
                              <Text>{municipality.description}</Text>
                            </div>
                          </Col>
                        )}
                        {municipality.translations && Object.keys(municipality.translations).length > 0 && (
                          <Col xs={24}>
                            <Text type="secondary">{t('settings.localization.translations', 'Translations')}</Text>
                            <div style={{ marginTop: '8px' }}>
                              {Object.keys(municipality.translations).map((lang) => {
                                const translation = municipality.translations![lang];
                                return (
                                  <Card key={lang} size="small" style={{ marginBottom: '8px' }}>
                                    <Text strong>{lang === 'en' ? 'English' : lang === 'el' ? 'Greek' : lang}</Text>
                                    {translation.name && (
                                      <div style={{ marginTop: '4px' }}>
                                        <Text type="secondary">{t('settings.localization.translatedName', 'Name')}: </Text>
                                        <Text>{translation.name}</Text>
                                      </div>
                                    )}
                                    {translation.address && (
                                      <div style={{ marginTop: '4px' }}>
                                        <Text type="secondary">{t('settings.localization.translatedAddress', 'Address')}: </Text>
                                        <Text>{translation.address}</Text>
                                      </div>
                                    )}
                                  </Card>
                                );
                              })}
                            </div>
                          </Col>
                        )}
                        {!municipality.primaryLanguage && !municipality.availableLanguages && !municipality.timezone && !municipality.description && (!municipality.translations || Object.keys(municipality.translations).length === 0) && (
                          <Col xs={24}>
                            <Empty
                              description={t('settings.localization.noData', 'No localization settings available')}
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                          </Col>
                        )}
                      </Row>
                    ) : (
                      <Empty
                        description={t('settings.localization.noData', 'No localization information available')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>

                  {/* 5. Social Media Links */}
                  <Card
                    title={
                      <Space>
                        <GlobalOutlined />
                        {t('settings.socialMedia.title', 'Social Media Links')}
                        {socialMediaData && (
                          <Tag color="blue">{socialMediaData.total}</Tag>
                        )}
                      </Space>
                    }
                  >
                    {socialMediaLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                      </div>
                    ) : socialMediaError ? (
                      <Alert
                        message={t('settings.socialMedia.loadError', 'Error loading social media links')}
                        description={socialMediaError}
                        type="error"
                        showIcon
                      />
                    ) : socialMediaData && socialMediaData.items.length > 0 ? (
                      <Row gutter={[16, 16]}>
                        {socialMediaData.items.map((link) => (
                          <Col xs={24} sm={12} md={8} key={link.id}>
                            <Card size="small">
                              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <div>
                                  <Tag color="blue" style={{ textTransform: 'capitalize' }}>
                                    {link.platform}
                                  </Tag>
                                </div>
                                {link.name && (
                                  <div>
                                    <Text strong>{link.name}</Text>
                                  </div>
                                )}
                                <div>
                                  <Text>
                                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                                      {link.url}
                                    </a>
                                  </Text>
                                </div>
                                <div>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {t('settings.socialMedia.createdAt', 'Created')}: {formatDateShort(link.createdAt)}
                                  </Text>
                                </div>
                              </Space>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <Empty
                        description={t('settings.socialMedia.noLinks', 'No social media links found')}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Card>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default MunicipalityDetails;
